import {
  Alert,
  Button,
  Dialog,
  Divider,
  OpenInNewIcon,
  Skeleton,
  Stack,
  Tag,
  Typography,
} from '@all-manager/design-system-v2';
import { type Props, useDocumentContentModal } from './useDocumentContentModal';


export default function DocumentContentModal(props: Props) {
  const hook = useDocumentContentModal(props);

  if (!props.open) return null;

  return (
    <Dialog
      open={props.open}
      onClose={hook.handleClose}
      maxWidth="md"
      fullWidth
      title={
        <Stack spacing={0.5}>
          <Typography variant="h3">{props.documentName}</Typography>
          <Typography variant="body2">Contenu textuel extrait par l'intelligence artificielle</Typography>
        </Stack>
      }
      closeLabel="Fermer"
      actions={{
        primaryAction: {
          label: 'Fermer',
          onClick: hook.handleClose,
        },
        secondaryAction: {
          label: hook.copied ? 'Copié !' : 'Copier le texte',
          onClick: hook.handleCopyContent,
          disabled: !hook.content,
        },
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center" wrap="wrap">
            {hook.formattedExtractedAt ? (
              <Tag
                color="default"
                label={`Indexé le ${hook.formattedExtractedAt}`}
              />
            ) : null}
            {hook.charCount > 0 ? (
              <Tag
                color="default"
                label={`${hook.charCount.toLocaleString('fr-FR')} caractères · ${hook.wordCount.toLocaleString('fr-FR')} mots`}
              />
            ) : null}
          </Stack>

          {props.fileUrl ? (
            <Button
              variant="text"
              size="small"
              color="default"
              startIcon={<OpenInNewIcon />}
              onClick={hook.handleOpenOriginal}
            >
              Ouvrir l'original
            </Button>
          ) : null}
        </Stack>

        <Divider />

        {hook.isLoading ? (
          <Stack spacing={1}>
            <Skeleton variant="text" width="90%" height={24} />
            <Skeleton variant="text" width="95%" height={24} />
            <Skeleton variant="text" width="80%" height={24} />
            <Skeleton variant="text" width="88%" height={24} />
            <Skeleton variant="text" width="60%" height={24} />
          </Stack>
        ) : hook.isError ? (
          <Alert severity="error">
            Le contenu extrait n'a pas pu être chargé. Veuillez réessayer ultérieurement.
          </Alert>
        ) : !hook.content ? (
          <Alert severity="info">
            Aucun contenu textuel n'a été extrait pour ce document.
          </Alert>
        ) : (
          <div
            style={{
              padding: 16,
              maxHeight: '55vh',
              overflowY: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: 1.6,
              userSelect: 'text',
              border: '1px solid #e0e0e0',
              borderRadius: 8,
              backgroundColor: '#fff',
            }}
          >
            <Typography variant="body2">
              {hook.content}
            </Typography>
          </div>
        )}

      </Stack>
    </Dialog>
  );
}
